// Kinio Node 開通（DEV-17，SPEC #vms-web-node-enroll、#provisioning-s3-8-org；Eddy 2026-10-12 選單，kinio/plan#78）。
//   - 誰：組織的 owner / org_admin，或 owner / org_admin 親自指派的 node_admin（/v1/me/orgs 的 node_enroll）；而且組織有開通額度或開通過節點
//   - 用節點 console `enroll_status` 那一行（或 fp= 後面 16 碼）找一筆待開通 → 閃燈 → 勾確認 → 開通；不給整份待開通清單
//   - 閃燈與開通後的狀態用讀單筆 API 每 10 秒重讀（不重打查詢 / 閃燈；不寫稽核、不計次數）
//   - 用詞照 UX-09：畫面不出現 AID（只有尾碼）、不寫環境名稱（改顯示本平台的報到網址）
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { nodeEnrollApi, type MyOrg, type NodeEnrollOverview, type NodeEnrollRequest } from '../../lib/planeApi';
import { errText } from '../settings/OrgSection';
import { parseEnrollLine, sameEnrollOrigin } from './inventoryModel';
import { stamp } from '../../lib/timefmt';
import { useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';

/** 日期時間（瀏覽器的時區；這頁的時間不屬於任何家戶）。 */
function fmtTime(iso: string, _lang: string): string {
  return stamp(iso);
}

/** 幾分鐘前（畫面用；超過一天顯示日期時間）。 */
function ago(t: (k: string, o?: Record<string, unknown>) => string, iso: string | undefined, lang: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return t('nenr.just_now');
  if (ms < 3_600_000) return t('nenr.min_ago', { n: Math.floor(ms / 60_000) });
  if (ms < 86_400_000) return t('nenr.hour_ago', { n: Math.floor(ms / 3_600_000) });
  return fmtTime(iso, lang);
}

/** 錯誤 → 文案（SPEC DEV-17「按鈕的錯誤」）。 */
function enrollErr(t: (k: string, o?: Record<string, unknown>) => string, e: unknown, lang: string): string {
  if (!isKinioError(e)) return errText(t, e);
  const until = (secs: unknown) => fmtTime(new Date(Date.now() + Number(secs ?? 0) * 1000).toISOString(), lang);
  switch (e.code) {
    case 'ENROLL_NOT_FOUND':
      return t('nenr.err_gone');
    case 'RATE_LIMITED':
      return e.detail?.reason === 'daily_enroll_cap' ? t('nenr.err_daily_cap', { at: until(e.detail?.retry_after_seconds) }) : t('nenr.err_lookup_limit', { at: until(e.detail?.retry_after_seconds) });
    case 'MAC_CONFLICT':
      return t('nenr.mac_conflict');
    case 'NO_QUOTA':
      return t('nenr.err_no_quota');
    case 'ALLOCATION_NOT_FOUND':
    case 'TYPE_MISMATCH':
      return t('nenr.err_alloc');
    case 'MAC_IN_USE':
      return t('nenr.err_mac_in_use');
    case 'FORBIDDEN':
      return t('nenr.err_forbidden');
    case 'UNAVAILABLE':
      return t('nenr.err_unavailable');
  }
  return errText(t, e);
}

export function NodeEnrollPanel({ org }: { org: MyOrg }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const ov = useQuery({ queryKey: ['node-enroll', org.org_id], queryFn: () => nodeEnrollApi.overview(org), retry: false, staleTime: 60_000 });
  const [text, setText] = useState('');
  const [found, setFound] = useState<{ req: NodeEnrollRequest; fp: string; keypwd?: boolean } | null>(null);
  const [lookErr, setLookErr] = useState<{ kind: 'not_found' | 'other'; text: string; fp8?: string; at?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [approvedAt, setApprovedAt] = useState<number | null>(null);
  const parsed = parseEnrollLine(text);
  const data = ov.data;
  const totalQuota = (data?.quota ?? []).reduce((n, q) => n + q.available, 0);
  const wrongPlatform = !!(parsed.url && data?.enroll_url && !sameEnrollOrigin(parsed.url, data.enroll_url));

  // 閃燈中 / 等節點報到 / 開通後等上線：每 10 秒讀單筆（不寫稽核、不計次數）
  const poll =
    !!found && (found.req.identify_state === 'waiting' || found.req.identify_state === 'blinking' || (found.req.status === 'approved' && !found.req.online));
  useEffect(() => {
    if (!poll || !found) return;
    const id = setInterval(() => {
      nodeEnrollApi
        .get(org, found.req.id, found.fp)
        .then((r) => setFound((cur) => (cur && cur.req.id === r.request.id ? { ...cur, req: r.request } : cur)))
        .catch(() => undefined);
    }, 10_000);
    return () => clearInterval(id);
  }, [poll, found, org]);

  async function lookup() {
    if (!parsed.fp || wrongPlatform) return;
    setBusy(true);
    setLookErr(null);
    setFound(null);
    setActErr('');
    setApprovedAt(null);
    try {
      const r = await nodeEnrollApi.lookup(org, text);
      setFound({ req: r.request, fp: parsed.fp, keypwd: parsed.keypwd });
    } catch (e) {
      if (isKinioError(e) && e.code === 'ENROLL_NOT_FOUND')
        setLookErr({ kind: 'not_found', text: t('nenr.not_found'), fp8: parsed.fp.slice(0, 8), at: new Date().toISOString() });
      else setLookErr({ kind: 'other', text: enrollErr(t, e, i18n.language) });
    } finally {
      setBusy(false);
    }
  }

  async function identify() {
    if (!found) return;
    setBusy(true);
    setActErr('');
    try {
      const r = await nodeEnrollApi.identify(org, found.req.id, found.fp);
      setFound({ ...found, req: r.request });
    } catch (e) {
      setActErr(enrollErr(t, e, i18n.language));
    } finally {
      setBusy(false);
    }
  }

  if (ov.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (ov.isError) return <p className="error">{enrollErr(t, ov.error, i18n.language)}</p>;
  if (!data) return null;

  return (
    <section className="card" data-testid="node-enroll">
      <h2 className="section-title">{t('nenr.title', { name: org.display_name })}</h2>
      {/* 開通額度 */}
      <div data-testid="nenr-quota">
        <h3 className="small">{t('nenr.quota_title')}</h3>
        {totalQuota === 0 ? (
          <p className="muted">{t('nenr.no_quota')}</p>
        ) : (
          <ul className="plain">
            {data.quota.map((q) => (
              <li key={q.type_code}>
                <details>
                  <summary>{t('nenr.quota_line', { type: q.type_code, n: q.available })}</summary>
                  <ul className="plain small muted">
                    {q.allocations.map((a) => (
                      <li key={a.allocation_id}>{t('nenr.alloc_line', { date: fmtTime(a.created_at, i18n.language), n: a.available })}</li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* 找節點 */}
      {totalQuota > 0 && (
        <div className="form" data-testid="nenr-find">
          <h3 className="small">{t('nenr.find_title')}</h3>
          <p className="muted small">{t('nenr.find_help')}</p>
          {data.enroll_url && <p className="muted small">{t('nenr.enroll_url', { url: data.enroll_url })}</p>}
          <div className="toolbar">
            <input
              className="input mono"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void lookup()}
              placeholder={t('nenr.find_placeholder')}
              aria-label={t('nenr.find_title')}
              data-testid="nenr-input"
            />
            <button className="btn btn-primary" disabled={busy || !parsed.fp || wrongPlatform} onClick={() => void lookup()} data-testid="nenr-lookup">
              {t('nenr.find')}
            </button>
          </div>
          <p className="muted small">{t('nenr.name_hint')}</p>
          {text.trim() && !parsed.fp && <p className="error small">{t('nenr.bad_fp')}</p>}
          {wrongPlatform && <p className="error small" data-testid="nenr-wrong-platform">{t('nenr.wrong_platform', { url: data.enroll_url })}</p>}
          {lookErr?.kind === 'other' && <p className="error">{lookErr.text}</p>}
          {lookErr?.kind === 'not_found' && (
            <div className="card warn" data-testid="nenr-not-found">
              <p>{lookErr.text}</p>
              <ol className="small">
                <li>{t('nenr.check_fp')}</li>
                <li>{t('nenr.check_pending')}</li>
                <li>{t('nenr.check_url', { url: data.enroll_url || '—' })}</li>
                <li>{t('nenr.check_quota')}</li>
              </ol>
              <p className="small muted">{t('nenr.contact', { fp8: lookErr.fp8, at: fmtTime(lookErr.at!, i18n.language) })}</p>
            </div>
          )}
          {found && (
            <FoundCard
              req={found.req}
              keypwdMissing={found.keypwd === false}
              busy={busy}
              approvedAt={approvedAt}
              onIdentify={() => void identify()}
              onApprove={() => setApproveOpen(true)}
              error={actErr}
            />
          )}
        </div>
      )}
      {found && (
        <ApproveDialog
          open={approveOpen}
          org={org}
          data={data}
          req={found.req}
          fp={found.fp}
          close={() => setApproveOpen(false)}
          done={(req) => {
            setFound({ ...found, req });
            setApprovedAt(Date.now());
            setApproveOpen(false);
            void qc.invalidateQueries({ queryKey: ['node-enroll', org.org_id] });
          }}
        />
      )}
      {/* 本組織開通過的 */}
      <div data-testid="nenr-approved">
        <h3 className="small">{t('nenr.approved_title')}</h3>
        {data.approved.length === 0 ? (
          <p className="muted">{t('nenr.approved_empty')}</p>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th scope="col">{t('nenr.col_at')}</th>
                  <th scope="col">MAC</th>
                  <th scope="col">{t('nenr.col_model')}</th>
                  <th scope="col">{t('nenr.col_by')}</th>
                  <th scope="col">{t('nenr.col_note')}</th>
                  <th scope="col">{t('nenr.col_state')}</th>
                </tr>
              </thead>
              <tbody>
                {data.approved.map((a) => (
                  <tr key={a.id} data-enroll={a.id}>
                    <td className="small">{a.approved_at ? fmtTime(a.approved_at, i18n.language) : '—'}</td>
                    <td className="mono small">{a.mac}</td>
                    <td className="small">{a.model}</td>
                    <td className="small">{a.approved_via === 'org' ? a.approved_by_name || t('nenr.by_member') : t('nenr.by_platform')}</td>
                    <td className="small">{a.note || '—'}</td>
                    <td className="small">
                      {a.aid_status && a.aid_status !== 'active' ? (
                        <span className="badge badge-err" title={t('nenr.revoked_hint')}>
                          {t('nenr.revoked')}
                        </span>
                      ) : a.online ? (
                        <span className="badge badge-ok">{t('nenr.online')}</span>
                      ) : (
                        <span className="muted">{t('nenr.last_online', { at: ago(t, a.last_online_at, i18n.language) })}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted small">{t('nenr.pair_hint')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function FoundCard({
  req,
  keypwdMissing,
  busy,
  approvedAt,
  onIdentify,
  onApprove,
  error,
}: {
  req: NodeEnrollRequest;
  keypwdMissing: boolean;
  busy: boolean;
  approvedAt: number | null;
  onIdentify(): void;
  onApprove(): void;
  error: string;
}) {
  const { t, i18n } = useTranslation();
  const stale = req.last_seen_at ? Date.now() - new Date(req.last_seen_at).getTime() > 3 * 60_000 : false;
  const approved = req.status === 'approved';
  const waitedLong = approvedAt !== null && Date.now() - approvedAt > 10 * 60_000;
  return (
    <div className="card" data-testid="nenr-card" data-status={req.status}>
      <dl className="kv small">
        <dt>{t('nenr.col_model')}</dt>
        <dd>{req.model}</dd>
        <dt>MAC</dt>
        <dd className="mono">{req.mac}</dd>
        <dt>{t('nenr.fw')}</dt>
        <dd>{req.fw_version || '—'}</dd>
        <dt>{t('nenr.last_seen')}</dt>
        <dd>
          {ago(t, req.last_seen_at, i18n.language)}
          {stale && !approved && <span className="error small"> {t('nenr.maybe_offline')}</span>}
        </dd>
      </dl>
      {req.mac_conflict && <p className="error">{t('nenr.mac_conflict')}</p>}
      {keypwdMissing && !approved && <p className="error">{t('nenr.keypwd_missing')}</p>}
      {req.identify_state && (
        <p className="small" data-testid="nenr-identify" data-state={req.identify_state}>
          {t('nenr.identify_' + req.identify_state)}
        </p>
      )}
      {approved ? (
        <p className={req.online ? 'ok' : 'muted'} data-testid="nenr-approved-state">
          {req.online ? t('nenr.now_online') : waitedLong ? t('nenr.not_online_long') : t('nenr.wait_online')}
        </p>
      ) : (
        <div className="toolbar">
          <button className="btn" disabled={busy} onClick={onIdentify} data-testid="nenr-identify-btn">
            {t('nenr.identify')}
          </button>
          <button className="btn btn-primary" disabled={busy || !!req.mac_conflict || keypwdMissing} onClick={onApprove} data-testid="nenr-approve-btn">
            {t('nenr.approve')}
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function ApproveDialog({
  open,
  org,
  data,
  req,
  fp,
  close,
  done,
}: {
  open: boolean;
  org: MyOrg;
  data: NodeEnrollOverview;
  req: NodeEnrollRequest;
  fp: string;
  close(): void;
  done(req: NodeEnrollRequest): void;
}) {
  const { t, i18n } = useTranslation();
  // 只分 TYPE、不分機種（SPEC #provisioning-type-quota）：用報到紀錄的 TYPE 對額度
  const allocs = useMemo(
    () => (req.type_code ? data.quota.find((q) => q.type_code === req.type_code)?.allocations.filter((a) => a.available > 0) : undefined) ?? [],
    [data, req.type_code],
  );
  const [alloc, setAlloc] = useState('');
  const [note, setNote] = useState('');
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const verify = useEmailVerifyPrompt(); // 403 EMAIL_NOT_VERIFIED（platform#176）
  const chosen = alloc || (allocs.length === 1 ? allocs[0].allocation_id : '');
  const left = allocs.find((a) => a.allocation_id === chosen)?.available ?? 0;
  const noteBad = note.trim().length > 200 || /[\u0000-\u001f]/.test(note);

  async function submit() {
    setBusy(true);
    setErr('');
    try {
      const r = await nodeEnrollApi.approve(org, req.id, { fp, allocation_id: chosen, note: note.trim() || undefined, confirm: true });
      done(r.request);
    } catch (e) {
      if (!verify.ask(e)) setErr(enrollErr(t, e, i18n.language));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} title={t('nenr.approve_title')} onClose={close}>
      {verify.prompt}
      <div className="form" data-testid="nenr-approve-dialog">
        {allocs.length === 0 ? (
          <p className="error">{t('nenr.err_no_quota')}</p>
        ) : (
          <label className="field">
            <span>{t('nenr.alloc')}</span>
            <select className="input" value={chosen} onChange={(e) => setAlloc(e.target.value)} data-testid="nenr-alloc">
              {allocs.length > 1 && <option value="">{t('nenr.alloc_pick')}</option>}
              {allocs.map((a) => (
                <option key={a.allocation_id} value={a.allocation_id}>
                  {t('nenr.alloc_line', { date: fmtTime(a.created_at, i18n.language), n: a.available })}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>{t('nenr.note')}</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder={t('nenr.note_placeholder')} data-testid="nenr-note" />
        </label>
        {noteBad && <p className="error small">{t('nenr.note_bad')}</p>}
        <p className="small">{t('nenr.approve_explain', { name: org.display_name, n: Math.max(left - 1, 0) })}</p>
        <label className="check">
          <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="nenr-confirm" /> {t('nenr.confirm')}
        </label>
        {err && <p className="error">{err}</p>}
        <div className="toolbar">
          <div className="spacer" />
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !ok || !chosen || noteBad} onClick={() => void submit()} data-testid="nenr-approve-submit">
            {t('nenr.approve_do')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
